import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { BUTTON_CONST } from '@/src/constants/buttons';
import PATH from '@/src/routes/path';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import DetailSection from '@/src/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import {
  getPushAlarmInfoByIdAPI,
  getPushAlarmInfoExcelAPI,
  getPushAppPageConnectionCategoryAPI,
} from '@/src/apis/rest/pushAlarm';
import { formFields } from '@/src/pages/pushAlarm/field';
import ContentsPreview from '@/components/molecule/ContentsPreview';
import Typo from '@/components/atom/Typo';
import ExcelDownloadButton from '@/src/components/atom/ExcelDownloadButton';
import {
  CONNECT_CONTENT_OPTIONS,
  OPTIONS_CONST,
} from '@/src/constants/options';

const BACK_PATH = PATH.PUSH_ALARM_LIST;
const EDIT_PATH = PATH.PUSH_ALARM_EDIT;

export default function PushAlarmDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: pushAlarmData } = useQuery({
    queryKey: ['push-alarm-detail', id],
    queryFn: () => getPushAlarmInfoByIdAPI(Number(id)),
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH, { replace: true });
      },
    },
  });

  const { data: pushAppPageCategoryTypeData } = useQuery({
    queryKey: ['push-app-page-category'],
    queryFn: () => {
      return getPushAppPageConnectionCategoryAPI();
    },
  });

  // event
  const handleGoBack = () => {
    navigate(BACK_PATH);
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonColor="outline"
            buttonSize="medium"
            onClick={handleGoBack}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          {pushAlarmData?.pushProgressStatus === 'scheduled' && (
            <Link to={EDIT_PATH.replace(':id', id || '')}>
              <Button className="w-40" buttonSize="medium">
                {BUTTON_CONST.editShort}
              </Button>
            </Link>
          )}
          {pushAlarmData?.pushProgressStatus &&
            pushAlarmData?.pushProgressStatus !== 'scheduled' && (
              <Button className="w-40" buttonSize="medium" disabled={true}>
                {BUTTON_CONST.editShort}
              </Button>
            )}
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.pushAlarmInfoManagement,
          NAV_CONST.pushAlarmInfoDetail,
        ]}
        pageTitle={NAV_CONST.pushAlarmInfoDetail}
      />
      <div className="mb-[100px] flex flex-col gap-4">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                푸시 알림 정보
              </span>
            </div>
          }
          data={pushAlarmData}
          contents={[
            [
              {
                key: formFields.pushAlarmType.name,
                lable: '발송 유형',
                valueSetter: data =>
                  data?.pushAlarmType === 'instant'
                    ? OPTIONS_CONST.pushInstant
                    : OPTIONS_CONST.pushReservation,
                render: DetailSctionRenderer.TEXT(),
              },
              {
                key: formFields.reservationTime.name,
                lable: '발송 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: 'pushProgressStatus',
                lable: '발송 결과',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: OPTIONS_CONST.pushScheduled,
                      key: 'scheduled',
                      badgeColor: 'lightYellow',
                    },
                    {
                      label: OPTIONS_CONST.pushInProgress,
                      key: 'inProgress',
                      badgeColor: 'lightGreen',
                    },
                    {
                      label: OPTIONS_CONST.pushCompleted,
                      key: 'completed',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: 'pushCategoryTypeKr',
                lable: '카테고리',
                render: DetailSctionRenderer.TEXT(),
              },
            ],
            [
              {
                key: '',
                lable: '대상 회원',
                valueSetter: () => '마케팅 수신 동의 회원',
                render: () => {
                  return (
                    <div className="flex gap-[8px]">
                      <Typo typoType="b16r" typoColor="gray6">
                        마케팅 수신 동의 회원
                      </Typo>
                      {pushAlarmData?.pushProgressStatus === 'completed' && (
                        <ExcelDownloadButton
                          buttonSize="xSmall"
                          fileName="푸시_알림_리스트"
                          downloadExcelAPI={getPushAlarmInfoExcelAPI}
                          searchParams={{ id }}
                        />
                      )}
                    </div>
                  );
                },
              },
              {
                key: 'pushAlarmContentType',
                lable: '컨텐츠 유형',
                valueSetter: data => {
                  return data?.pushAlarmContentId
                    ? '컨텐츠 상세 연결'
                    : '앱 내 화면';
                },
                render: DetailSctionRenderer.TEXT(),
              },
              {
                key: 'contentTitle',
                lable: '연결 컨텐츠',
                valueSetter: data => {
                  if (data?.pushAlarmContentId) {
                    const contentCategory = data?.pushAlarmContentCategory
                      ? CONNECT_CONTENT_OPTIONS[data.pushAlarmContentCategory]
                      : '-';
                    return `[${contentCategory}] ${data?.contentTitle}`;
                  } else if (data?.pushAppPageConnectionCategory) {
                    const contentCategory = pushAppPageCategoryTypeData?.find(
                      item => item?.key === data.pushAppPageConnectionCategory,
                    )?.value;
                    return `${contentCategory}`;
                  }
                  return '-';
                },
              },
              {
                key: 'createdAt',
                lable: '등록 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '등록 관리자',
                render: DetailSctionRenderer.TEXT(),
              },
            ],
          ]}
          bottomContents={
            <ContentsPreview
              title={pushAlarmData?.pushAlarmTitle}
              contents={pushAlarmData?.pushAlarmContent}
              height={300}
              contentMinHeight={216}
            />
          }
        />
      </div>
      {renderBottomFixedContent()}
    </>
  );
}
